import { memo, useCallback, useEffect, useState } from 'react';
import type { PublishStatusNode } from '@/lib/types-publish-gate';
import { StatusIcon, resolveCellDisplayStatus, statusLabel, statusBgClass, ALL_STATUSES } from './StatusIcon';

const cellKey = (nodeId: string, month: string) => `${nodeId}|${month}`;
const monthDisplay = (m: string) => `${m.slice(0, 4)}/${m.slice(5, 7)}`;

type Props = {
  nodes: PublishStatusNode[];
  months: string[];
  selectedCells: Set<string>;
  onToggleSelect: (nodeId: string, month: string) => void;
  onCellClick: (nodeId: string, month: string) => void;
  canSelect: boolean;
};

const Legend = () => (
  <div className="flex flex-wrap gap-3 text-[12px] text-text-sub" role="note">
    {ALL_STATUSES.map((s) => (
      <span key={s} className="inline-flex items-center gap-1">
        <StatusIcon status={s} size={14} />
        {statusLabel(s)}
      </span>
    ))}
  </div>
);

type MatrixRowProps = {
  node: PublishStatusNode;
  selectedCells: Set<string>;
  onToggleSelect: (nodeId: string, month: string) => void;
  onCellClick: (nodeId: string, month: string) => void;
  canSelect: boolean;
  cellAriaLabel: (nodeName: string, month: string, statusText: string) => string;
  handleCellKeyDown: (e: React.KeyboardEvent, nodeId: string, month: string) => void;
};

/** 659店舗規模のテナントで店舗×月のセルを毎回全件再構築しないよう、行単位で
 *  React.memo 化する (2026-08-11)。selectedCells は Set の参照ごとチェック/選択のたびに
 *  丸ごと差し替わるため行の再レンダー自体は避けられないが、`nodes`/`months` が変わらない
 *  再レンダー (親ページの無関係な state 変化やクエリの再取得等) では各行の再構築をスキップできる。 */
const MatrixRow = memo(function MatrixRow({
  node, selectedCells, onToggleSelect, onCellClick, canSelect, cellAriaLabel, handleCellKeyDown,
}: MatrixRowProps) {
  return (
    <tr className="border-t border-surface-line">
      <th scope="row" className="text-left text-[13px] font-bold text-text-main px-3 py-2 whitespace-nowrap sticky left-0 bg-white">
        {node.node_name}
      </th>
      {node.cells.map((cell) => {
        const dispStatus = resolveCellDisplayStatus(cell);
        const key = cellKey(node.node_id, cell.target_month);
        const selected = selectedCells.has(key);
        return (
          <td key={cell.target_month} className={`px-1 py-1 text-center ${statusBgClass(dispStatus)}`}>
            <div className="inline-flex items-center gap-1">
              {canSelect && (
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => onToggleSelect(node.node_id, cell.target_month)}
                  aria-label={`${cellAriaLabel(node.node_name, cell.target_month, statusLabel(dispStatus))} を選択`}
                  className="h-4 w-4 focus-ring"
                />
              )}
              <button
                type="button"
                onClick={() => onCellClick(node.node_id, cell.target_month)}
                onKeyDown={(e) => handleCellKeyDown(e, node.node_id, cell.target_month)}
                aria-label={cellAriaLabel(node.node_name, cell.target_month, statusLabel(dispStatus))}
                className="p-1.5 rounded focus-ring hover:bg-black/5"
              >
                <StatusIcon status={dispStatus} />
              </button>
            </div>
          </td>
        );
      })}
    </tr>
  );
});

type MatrixCardProps = {
  node: PublishStatusNode;
  selectedCells: Set<string>;
  onToggleSelect: (nodeId: string, month: string) => void;
  onCellClick: (nodeId: string, month: string) => void;
  canSelect: boolean;
  cellAriaLabel: (nodeName: string, month: string, statusText: string) => string;
};

const MatrixCard = memo(function MatrixCard({
  node, selectedCells, onToggleSelect, onCellClick, canSelect, cellAriaLabel,
}: MatrixCardProps) {
  return (
    <div className="rounded-lg border border-surface-border bg-white p-3">
      <div className="text-[14px] font-extrabold text-text-main mb-2">{node.node_name}</div>
      <ul className="space-y-1.5">
        {node.cells.map((cell) => {
          const dispStatus = resolveCellDisplayStatus(cell);
          const key = cellKey(node.node_id, cell.target_month);
          const selected = selectedCells.has(key);
          return (
            <li key={cell.target_month} className={`flex items-center justify-between rounded px-2 py-1.5 ${statusBgClass(dispStatus)}`}>
              <div className="flex items-center gap-2 min-w-0">
                {canSelect && (
                  <input
                    type="checkbox"
                    checked={selected}
                    onChange={() => onToggleSelect(node.node_id, cell.target_month)}
                    aria-label={`${cellAriaLabel(node.node_name, cell.target_month, statusLabel(dispStatus))} を選択`}
                    className="h-5 w-5 shrink-0 focus-ring"
                  />
                )}
                <span className="text-[13px] font-bold text-text-body">{monthDisplay(cell.target_month)}</span>
              </div>
              <button
                type="button"
                onClick={() => onCellClick(node.node_id, cell.target_month)}
                aria-label={cellAriaLabel(node.node_name, cell.target_month, statusLabel(dispStatus))}
                className="inline-flex items-center gap-1.5 px-2 py-1 rounded focus-ring min-h-[36px]"
              >
                <StatusIcon status={dispStatus} />
                <span className="text-[12px] text-text-sub">{statusLabel(dispStatus)}</span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
});

/**
 * v0.3 (S-7): 公開状態マトリクス。
 * Design H4対応: タブレット+ (>=640px) はテーブル、モバイル (<640px) は月pill選択+カード縦積みに切替。
 * Design C2対応 (WCAG 1.4.1): 全セルに aria-label、キーボード操作 (Tab/Space/Enter) 対応、凡例を <caption> 相当で提供。
 */
export const PublishStatusMatrix = ({ nodes, months, selectedCells, onToggleSelect, onCellClick, canSelect }: Props) => {
  // デスクトップ表/モバイルカードを CSS の hidden/sm:hidden だけで出し分けると、実際には
  // 両方が常時 DOM 構築される (659店舗規模で二重にコストがかかる)。matchMedia で実際の
  // ビューポートに応じてどちらか一方だけをレンダリングする (StoreMultiSelect 等と同一パターン、2026-08-11)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 639px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 639px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // MatrixRow/MatrixCard (React.memo) に渡す関数は毎レンダー新規参照だとmemoが常に無効化される
  // ため useCallback で安定化する (QA/design-and-codeレビュー指摘、2026-08-11)
  const cellAriaLabel = useCallback(
    (nodeName: string, month: string, statusText: string) => `${nodeName} ${monthDisplay(month)} ${statusText}`,
    [],
  );

  const handleCellKeyDown = useCallback((e: React.KeyboardEvent, nodeId: string, month: string) => {
    if (e.key === 'Enter') { e.preventDefault(); onCellClick(nodeId, month); }
    if (e.key === ' ') { e.preventDefault(); if (canSelect) onToggleSelect(nodeId, month); }
  }, [onCellClick, onToggleSelect, canSelect]);

  return (
    <div className="space-y-3">
      <Legend />

      {isMobile ? (
        /* モバイル (<640px): カード縦積み (Design H4)。店マネージャーは自店舗1行のみで matrix 不要なため */
        <div className="space-y-4">
          {nodes.map((node) => (
            <MatrixCard
              key={node.node_id}
              node={node}
              selectedCells={selectedCells}
              onToggleSelect={onToggleSelect}
              onCellClick={onCellClick}
              canSelect={canSelect}
              cellAriaLabel={cellAriaLabel}
            />
          ))}
        </div>
      ) : (
        /* デスクトップ/タブレット (>=640px): テーブル。横スクロールは専用コンテナに閉じ込める (ui-guidelines §10) */
        <div className="overflow-x-auto rounded-lg border border-surface-border">
          <table className="min-w-full border-collapse" aria-label="店舗別公開状態マトリクス">
            <caption className="sr-only">店舗×月ごとの公開状態。●=公開済み、◐=一部入力中、○=未着手、―=対象外</caption>
            <thead>
              <tr className="bg-surface-sunken sticky top-0 z-10">
                <th scope="col" className="text-left text-[12px] font-extrabold text-text-sub px-3 py-2 sticky left-0 bg-surface-sunken">店舗</th>
                {months.map((m) => (
                  <th key={m} scope="col" className="text-center text-[12px] font-extrabold text-text-sub px-3 py-2 whitespace-nowrap">
                    {monthDisplay(m)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {nodes.map((node) => (
                <MatrixRow
                  key={node.node_id}
                  node={node}
                  selectedCells={selectedCells}
                  onToggleSelect={onToggleSelect}
                  onCellClick={onCellClick}
                  canSelect={canSelect}
                  cellAriaLabel={cellAriaLabel}
                  handleCellKeyDown={handleCellKeyDown}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
